import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Input,
  Modal,
  SegmentedTabs,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import { errorMessage, fieldErrors } from '@/hooks/useApiError';

import {
  COUPON_AUDIENCES,
  COUPON_SERVER_FIELDS,
  audienceLabel,
  couponChanges,
  couponCreateBody,
  couponPlanOptions,
  couponToForm,
  defaultCurrency,
  todayInput,
  validateCoupon,
} from './couponForm';
import { BILLING_CURRENCIES } from './money';
import { PlanMultiSelect } from './PlanMultiSelect';

/**
 * Create a coupon, or edit one in place.
 *
 * Who it is for -- companies or students -- is chosen first because
 * everything after it depends on it: the plans it can be restricted to and
 * the currency a fixed amount is in. Like the code, it can change only until
 * somebody uses the coupon.
 *
 * What a coupon is worth (the amount and how long it applies) is frozen once
 * somebody has redeemed it -- the server answers 409 -- so on edit those
 * fields are disabled then rather than offered and refused. Only fields that
 * changed are sent.
 *
 * Render with `key` set to the coupon's code (or "new") so a second coupon
 * starts from its own values.
 */
export function CouponFormModal({ coupon, onClose }) {
  const isEdit = Boolean(coupon);
  const today = todayInput();
  /** The earliest expiry: today (a coupon works through its expiry day). */
  const earliestExpiry = today;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState(() => couponToForm(coupon));
  const [errors, setErrors] = useState({});

  const plans = useQuery({ queryKey: ['admin-plans'], queryFn: adminApi.listBillingPlans });
  const options = couponPlanOptions(plans.data, form.audience, form.plan_codes);

  const frozen = isEdit && coupon.times_redeemed > 0;
  const percentage = form.discount_type === 'PERCENTAGE';
  const isStudent = form.audience === 'STUDENT';

  function update(key, value) {
    setForm((previous) => ({ ...previous, [key]: value }));
    // An edited field's error goes away; the rest stay until the next save.
    setErrors((previous) => (previous[key] ? { ...previous, [key]: undefined } : previous));
  }
  const set = (key) => (event) => update(key, event.target.value);

  /** A plan chosen for the other audience would be refused, so it goes. */
  function chooseAudience(audience) {
    setForm((previous) => ({
      ...previous,
      audience,
      plan_codes: previous.plan_codes.filter((code) =>
        (plans.data ?? []).some((plan) => plan.code === code && plan.audience === audience),
      ),
      currency:
        previous.currency === defaultCurrency(previous.audience)
          ? defaultCurrency(audience)
          : previous.currency,
    }));
    setErrors({});
  }

  const editBody = isEdit ? couponChanges(coupon, form) : null;
  const nothingChanged = isEdit && Object.keys(editBody).length === 0;

  const save = useMutation({
    mutationFn: () =>
      isEdit
        ? adminApi.updateCoupon(coupon.code, editBody)
        : adminApi.createCoupon(couponCreateBody(form)),
    onSuccess: (saved) => {
      toast.success(isEdit ? `${saved.code} updated` : `${saved.code} created`);
      void queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
      onClose();
    },
    onError: (error) => {
      // A refusal that names its field is shown under that field.
      const byField = Object.fromEntries(
        Object.entries(fieldErrors(error)).map(([key, message]) => [
          COUPON_SERVER_FIELDS[key] ?? key,
          message,
        ]),
      );
      if (Object.keys(byField).length > 0) setErrors(byField);
      // A toast only for what has no field on this form to sit under.
      if (Object.keys(byField).every((key) => !(key in form) && key !== 'audience')) {
        toast.error(errorMessage(error, 'Could not save the coupon'));
      }
    },
  });

  function submit(event) {
    event.preventDefault();
    const found = validateCoupon(form, {
      isEdit,
      saved: isEdit ? couponToForm(coupon) : null,
      timesRedeemed: coupon?.times_redeemed ?? 0,
      plans: plans.data ?? [],
    });
    setErrors(found);
    if (Object.keys(found).length === 0) save.mutate();
  }

  return (
    <Modal
      open
      size="lg"
      title={isEdit ? `Edit ${coupon.code}` : 'New coupon'}
      description={
        frozen
          ? 'Used already, so the code, who it is for and the discount are fixed. Retire and restore from the list.'
          : isEdit
            ? 'Nobody has used it yet, so everything can still change.'
            : 'The code and what it is worth are fixed once somebody redeems it, so read it back before saving.'
      }
      onClose={onClose}
    >
      <form onSubmit={submit} noValidate className="divide-line divide-y">
        <Section title="For">
          {frozen ? (
            <p className="text-ink-muted flex items-center gap-2 text-sm">
              <Badge tone={isStudent ? 'brand' : 'neutral'}>{audienceLabel(form.audience)}</Badge>
              Fixed now that the coupon has been used.
            </p>
          ) : (
            <>
              <SegmentedTabs
                variant="plain"
                label="Who the coupon is for"
                tabs={COUPON_AUDIENCES}
                value={form.audience}
                onChange={chooseAudience}
              />
              <p className="text-ink-muted mt-2 text-xs">
                {isStudent
                  ? 'Students enter it when they upgrade their plan.'
                  : 'Companies enter it when they change plan.'}
              </p>
            </>
          )}
        </Section>

        <Section title="Coupon">
          <div className="space-y-4">
            {frozen ? (
              <p className="text-ink-muted flex items-center gap-2 text-sm">
                Code <span className="text-ink font-mono font-semibold">{coupon.code}</span>· fixed
                now that it has been used.
              </p>
            ) : (
              <Input
                label="Code"
                required
                maxLength={40}
                autoComplete="off"
                hint="3–40 letters, numbers, - or _. Saved in capitals; customers can type it either way."
                className="font-mono uppercase"
                value={form.code}
                onChange={set('code')}
                error={errors.code}
              />
            )}
            <Textarea
              label="Description"
              rows={2}
              maxLength={2000}
              hint="For your team. Never shown to the customer."
              value={form.description}
              onChange={set('description')}
              error={errors.description}
            />
          </div>
        </Section>

        <Section title="Discount">
          {frozen && (
            <Alert tone="info" className="mb-4">
              Redeemed {coupon.times_redeemed} {coupon.times_redeemed === 1 ? 'time' : 'times'}, so
              what it is worth can no longer change. To offer a different discount, retire it and
              create a new code.
            </Alert>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Kind"
              required
              disabled={frozen}
              value={form.discount_type}
              onChange={set('discount_type')}
              error={errors.discount_type}
            >
              <option value="PERCENTAGE">Percentage off</option>
              <option value="FIXED">Fixed amount off</option>
            </Select>
            <Select
              label="Applies to"
              required
              disabled={frozen}
              value={form.duration}
              onChange={set('duration')}
              error={errors.duration}
            >
              <option value="ONCE">The first bill only</option>
              <option value="FOREVER">Every renewal</option>
            </Select>
            {percentage ? (
              <Input
                label="Percent off"
                required
                inputMode="numeric"
                disabled={frozen}
                value={form.percent_off}
                onChange={set('percent_off')}
                error={errors.percent_off}
                hint="1 to 100"
              />
            ) : (
              <>
                <Input
                  label={`Amount off (${form.currency.trim().toUpperCase() || 'currency'})`}
                  required
                  inputMode="decimal"
                  disabled={frozen}
                  value={form.amount_off}
                  onChange={set('amount_off')}
                  error={errors.amount_off}
                  hint="Up to 2 decimals. More than the price makes the period free, never a credit."
                />
                {!frozen && (
                  <Select
                    label="Currency"
                    required
                    value={form.currency.trim().toUpperCase()}
                    onChange={set('currency')}
                    error={errors.currency}
                    hint={
                      isStudent
                        ? 'Student plans are billed in INR.'
                        : 'Refused rather than converted if a plan is billed in another.'
                    }
                  >
                    {(BILLING_CURRENCIES.includes(form.currency.trim().toUpperCase())
                      ? BILLING_CURRENCIES
                      : [form.currency.trim().toUpperCase(), ...BILLING_CURRENCIES]
                    )
                      .filter(Boolean)
                      .map((currency) => (
                        <option key={currency} value={currency}>
                          {currency}
                        </option>
                      ))}
                  </Select>
                )}
              </>
            )}
          </div>
        </Section>

        <Section title="Limits" description="Optional, except uses per customer (1 by default).">
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                label="Starts"
                type="date"
                // Past days are closed. A coupon already running keeps its own
                // start, which stays shown and saves unchanged (validateCoupon).
                min={today}
                value={form.starts_at}
                onChange={set('starts_at')}
                error={errors.starts_at}
                hint="Empty for now."
              />
              <Input
                label="Expires"
                type="date"
                // Never in the past, and not before the start when there is one.
                min={
                  form.starts_at && form.starts_at > earliestExpiry
                    ? form.starts_at
                    : earliestExpiry
                }
                value={form.expires_at}
                onChange={set('expires_at')}
                error={errors.expires_at}
                hint="Empty for no end date."
              />
              <Input
                label="Maximum redemptions"
                inputMode="numeric"
                value={form.max_redemptions}
                onChange={set('max_redemptions')}
                error={errors.max_redemptions}
                hint={
                  isEdit
                    ? `In total, across all customers. Empty for unlimited. Used ${coupon.times_redeemed} so far.`
                    : 'In total, across all customers. Empty for unlimited.'
                }
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                label="Uses per customer"
                required
                inputMode="numeric"
                value={form.max_uses_per_customer}
                onChange={set('max_uses_per_customer')}
                error={errors.max_uses_per_customer}
                hint="How many times one company or student may use it. Usually 1."
              />
            </div>
            <PlanMultiSelect
              label="Restrict to plans"
              plans={options}
              value={form.plan_codes}
              onChange={(codes) => update('plan_codes', codes)}
              error={errors.plan_codes}
              hint={
                plans.isError
                  ? 'Could not load the plans.'
                  : `Only ${isStudent ? 'student' : 'company'} plans with a price are listed.`
              }
              noPlansLabel={
                plans.isPending
                  ? 'Loading plans…'
                  : `There are no paid ${isStudent ? 'student' : 'company'} plans yet.`
              }
            />
          </div>
        </Section>

        {errors.audience && (
          <Alert tone="error" className="mt-4">
            {errors.audience}
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-5">
          <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={nothingChanged} isLoading={save.isPending}>
            {isEdit ? 'Save changes' : 'Create coupon'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** One titled block of the form, separated from the next by a hairline. */
function Section({ title, description, children }) {
  return (
    <section className="py-5 first:pt-0">
      <div className="mb-3">
        <h3 className="text-ink text-[13px] font-semibold">{title}</h3>
        {description && <p className="text-ink-muted mt-0.5 text-xs">{description}</p>}
      </div>
      {children}
    </section>
  );
}
